import axios from 'axios';
import { createApiClient } from '../../api/client';
import { deriveRange } from './dateRange';
import type { PaymentDetailResponse, PaymentFilters, PaymentListResponse } from './types';

/**
 * pay-by-bank-backoffice talks to the `pispbo` REST API (`/api/v1/pispbo/*`).
 * Contract source: `flows/pay-by-bank-backoffice/API_CONTRACT.md` (authoritative
 * — PRD §7).
 *
 * Auth: the host-injected operator JWT (carrying the
 * `CAN_SEE_CUSTOM_PAY_BY_BANK_BACKOFFICE` authority) travels in the
 * `Authorization` header, attached by `createApiClient` — never in the body.
 * `customerId` is read from the JWT `cu` claim server-side, never sent as a
 * param.
 *
 * Base URL is `VITE_API_URL` from `.env.<mode>` — empty in dev / unit tests so
 * MSW (relative-path handlers) intercepts; per-env builds point it at the real
 * service.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';
export const PAYMENTS_PATH = '/api/v1/pispbo/payments';
export const paymentDetailPath = (revenueId: string): string =>
  `${PAYMENTS_PATH}/${encodeURIComponent(revenueId)}`;

const apiClient = () => createApiClient(API_BASE);

/** Build the `GET /payments` query params from the in-memory filter state. */
export const buildListParams = (
  filters: PaymentFilters,
  language?: string,
): Record<string, string | number> => {
  const { fromDate, toDate } = deriveRange(filters.dateRangePreset, {
    fromDate: filters.fromDate,
    toDate: filters.toDate,
  });
  const params: Record<string, string | number> = { page: filters.page };
  if (filters.externalClientId) params.externalClientId = filters.externalClientId;
  if (filters.clientId) params.clientId = filters.clientId;
  if (filters.status) params.status = filters.status;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  if (language) params.lang = language;
  return params;
};

/** List payments + aggregates for the operator's customer (PRD §7). */
export const fetchPayments = async (
  filters: PaymentFilters,
  language?: string,
): Promise<PaymentListResponse> => {
  const { data } = await apiClient().get<PaymentListResponse>(PAYMENTS_PATH, {
    params: buildListParams(filters, language),
  });
  return data;
};

/** One payment's consolidated detail (revenue + live Volt) (PRD §7). */
export const fetchPaymentDetail = async (revenueId: string): Promise<PaymentDetailResponse> => {
  const { data } = await apiClient().get<PaymentDetailResponse>(paymentDetailPath(revenueId));
  return data;
};

// --- Error extraction ------------------------------------------------------

export type ApiError = {
  /** HTTP status (undefined for a transport/network error with no response). */
  status?: number;
  /** RFC-7807 `errorCode` when present (e.g. `PAYMENT_NOT_FOUND`, `FORBIDDEN`). */
  errorCode: string | null;
};

type ProblemDetail = { errorCode?: string };

/** Normalise an unknown thrown error into `{ status, errorCode }`. */
export const parseApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ProblemDetail | undefined;
    return { status: error.response?.status, errorCode: body?.errorCode ?? null };
  }
  return { errorCode: null };
};
